import React, { useEffect, useState } from "react";
import Pagination from "../Pagination";
import api from "../../../utilis/api";
import { toast } from "react-toastify";

const FaqTable = ({ currentPage, totalPages, onPageChange }) => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false); // track edit mode
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    page: "",
    section: "",
    question: "",
    answer: "",
  });

  // ✅ GET FAQs
  const getFaqs = async () => {
    try {
      const res = await api.get("/faq");
      if (res?.data) {
        setData(res.data.data);
      }
    } catch (error) {
      toast.error(error?.message || "Error fetching FAQs");
    }
  };

  useEffect(() => {
    getFaqs();
  }, []);

  // ✅ HANDLE INPUT CHANGE
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ ADD OR UPDATE FAQ
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit && editId) {
        const res = await api.put(`/faq/${editId}`, form);
        if (res.data.success) {
          toast.success("FAQ updated successfully");
        }
      } else {
        const res = await api.post("/faq", form);
        if (res.data.success) {
          toast.success("FAQ added successfully");
        }
      }

      setShowModal(false);
      setIsEdit(false);
      setEditId(null);
      setForm({ page: "", section: "", question: "", answer: "" });
      getFaqs(); // refresh list
    } catch (error) {
      toast.error(error?.message || "Error saving FAQ");
    }
  };

  // ✅ OPEN EDIT MODAL
  const handleEdit = (faq) => {
    setForm({
      page: faq.page,
      section: faq.section,
      question: faq.question,
      answer: faq.answer,
    });
    setEditId(faq._id);
    setIsEdit(true);
    setShowModal(true);
  };

  // ✅ SOFT DELETE FAQ
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      const res = await api.delete(`/faq/${id}`);
      if (res.data.success) {
        toast.success("FAQ deleted successfully");
        getFaqs(); // refresh list
      }
    } catch (error) {
      toast.error(error?.message || "Error deleting FAQ");
    }
  };

  return (
    <div className="p-4">
      {/* 🔥 HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl text-white font-bold">Manage FAQs</h2>
        <button
          onClick={() => {
            setShowModal(true);
            setIsEdit(false);
            setForm({ page: "", section: "", question: "", answer: "" });
          }}
          className="px-4 py-2 bg-green-500 rounded hover:bg-green-600"
        >
          Add FAQ
        </button>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-700 text-white rounded-lg">
          <thead>
            <tr className="text-left border-b border-gray-600">
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Question</th>
              <th className="px-4 py-3">Answer</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <tr
                  key={item._id}
                  className="border-b border-gray-600 hover:bg-gray-600 transition"
                >
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3">{item.question}</td>
                  <td className="px-4 py-3">{item.answer}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button
                      className="px-2 py-1 bg-blue-500 rounded hover:bg-blue-600"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-2 py-1 bg-red-500 rounded hover:bg-red-600"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-4">
                  No FAQs found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ✅ Pagination */}
        {data.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {/* 🔥 MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md text-white">
            <h3 className="text-xl font-bold mb-4">
              {isEdit ? "Edit FAQ" : "Add FAQ"}
            </h3>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="text"
                name="page"
                placeholder="Page"
                value={form.page}
                onChange={handleChange}
                className="p-2 bg-gray-700 rounded"
              />

              <input
                type="text"
                name="section"
                placeholder="Section"
                value={form.section}
                onChange={handleChange}
                className="p-2 bg-gray-700 rounded"
              />

              <input
                type="text"
                name="question"
                placeholder="Question"
                value={form.question}
                onChange={handleChange}
                required
                className="p-2 bg-gray-700 rounded"
              />

              <textarea
                name="answer"
                placeholder="Answer"
                value={form.answer}
                onChange={handleChange}
                required
                className="p-2 bg-gray-700 rounded"
              />

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-red-500 rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 rounded"
                >
                  {isEdit ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqTable;
